import type { ApiClient, Appointment } from "@shyne/shared/api";
import type { uuid } from "@/interfaces";
import { appointment_status } from "@/models/appointment.model";
import { CANCELLABLE_STATUSES } from "./appointment-cancellation";
import { typedMoneyProblem } from "./typed-money";

export const ASSIGNABLE_STATUSES: readonly appointment_status[] = [
  ...CANCELLABLE_STATUSES,
  appointment_status.inprogress,
  appointment_status.done,
  appointment_status.inspected,
];

export const isAssignable = (status: appointment_status): boolean => ASSIGNABLE_STATUSES.includes(status);

export const isUnassignable = (status: appointment_status, hasAssignment: boolean): boolean => hasAssignment && CANCELLABLE_STATUSES.includes(status);

export type AssigneeType = "individual" | "sub";

export type ResourceAssignment = { type: AssigneeType; resourceId: uuid; pay: number | undefined };

export const mayGiveWorker = (status: appointment_status, hasWorker: boolean): boolean =>
  hasWorker ? CANCELLABLE_STATUSES.includes(status) : isAssignable(status);

export const mayChangeWorkerPay = (status: appointment_status, hasWorker: boolean): boolean => hasWorker && isAssignable(status);

export type AssigningHat = "vendor" | "subcontractor";

type CurrentAssignees = {
  assigned_user?: { id: uuid | null } | null;
  assigned_organization?: { id: uuid | null } | null;
};

export function currentAssigneeOf(hat: AssigningHat, { assigned_user, assigned_organization }: CurrentAssignees, listed: readonly uuid[]): uuid {
  const current = hat === "vendor" ? (assigned_organization?.id ?? assigned_user?.id) : assigned_user?.id;
  return current != null && listed.includes(current) ? current : "";
}

type CurrentAssignment = {
  assigned_user?: { id: uuid | null } | null;
  assigned_organization?: { id: uuid | null } | null;
  individual_payment?: number | null;
  subs_payment?: number | null;
};

export function currentPayOf(selected: uuid, current: CurrentAssignment): number | null {
  if (selected === "") return null;
  if (current.assigned_user?.id === selected) return current.individual_payment ?? null;
  if (current.assigned_organization?.id === selected) return current.subs_payment ?? null;
  return null;
}

const samePay = (typed: number | undefined, current: number | null | undefined) => typed === undefined || typed === current;

export function assignmentChangesNothing(hat: AssigningHat, { type, resourceId, pay }: ResourceAssignment, current: CurrentAssignment): boolean {
  if (type === "sub") return current.assigned_organization?.id === resourceId && samePay(pay, current.subs_payment);
  const sameWorker = current.assigned_user?.id === resourceId && (hat === "subcontractor" || !current.assigned_organization?.id);
  return sameWorker && samePay(pay, current.individual_payment);
}

export function typedPay(text: string): number | undefined | null {
  const trimmed = text.trim();
  if (trimmed === "") return undefined;
  return typedMoneyProblem(trimmed) === null ? Number(trimmed) : null;
}

type AssignmentApi = Pick<ApiClient["appointments"], "assign" | "unassign">;

const actingIn = (actingOrganization: uuid | null) => (actingOrganization ? { actingOrganization } : {});

export function createAppointmentAssignment(api: AssignmentApi) {
  return {
    assign(id: uuid, { type, resourceId, pay }: ResourceAssignment, actingOrganization: uuid | null): Promise<Appointment> {
      return api.assign(id, {
        ...(type === "sub" ? { subcontractor: resourceId } : { worker: resourceId }),
        ...(pay === undefined ? {} : { pay }),
        ...actingIn(actingOrganization),
      });
    },

    unassign(id: uuid, actingOrganization: uuid | null): Promise<Appointment> {
      return api.unassign(id, actingIn(actingOrganization));
    },
  };
}
